/* global Sys, Services, Resources, Utils */
Sys.ns("Services");

/**
 * The Game Information Service.
 *
 * @class Services.GameInformation
 * @extends Services.Base
 */
Services.GameInformation = {
    constructor: function() {
        var queryData = Resources.readData("queryData"),
            jurisdictionCode,
            folderOffset = "../",
            allowedJurisdictionCodesForGameInformation =
                Utils.CONSTANTS.JURISDICTION_SPECIFIC_CODES
                    .SHOW_GAME_INFORMATION,
            enableGameInformation = false;

        Services.GameInformation.superclass.constructor.apply(this, arguments);

        /* BUILD_START*/
        folderOffset = "../" + folderOffset;

        /* BUILD_END*/

        this._gameClientManifestURL = folderOffset + "META-INF/MANIFEST.MF";

        jurisdictionCode = queryData.jurisdictionCode;
        enableGameInformation =
            typeof jurisdictionCode !== "undefined" &&
            allowedJurisdictionCodesForGameInformation.some(function(code) {
                return code === jurisdictionCode;
            });

        this._enabled = enableGameInformation;

        this._minBet = null;
        this._maxBet = null;
        this._gameClientVersion = null;
        this._gameServerVersion = null;

        if (enableGameInformation) {
            this.assignGameInformation();
            // Min and max bet values can differ between free rounds and normal game rounds
            this.observable.on({
                "notify:freeRounds.started": this.assignGameInformation.bind(this),
                "notify:freeRounds.ended": this.assignGameInformation.bind(this)
            });
        }
    },

    /**
     * Gets the game information.
     *
     * @returns {Object} An object with the keys minBet (number), maxBet (number),  gameClientVersion (string), gameServerVersion (string).
     */
    getGameInformation: function() {
        return this._enabled ?
            {
                minBet: this._minBet,
                maxBet: this._maxBet,
                gameClientVersion: this._gameClientVersion,
                gameServerVersion: this._gameServerVersion
            } :
            {
                minBet: null,
                maxBet: null,
                gameClientVersion: null,
                gameServerVersion: null
            };
    },

    /**
     * Sets the min and max bet to new values if the default calculation would be insufficient.
     *
     * @param {Object} minMaxBetCents An object with the keys minBet (number) and maxBet (number), values are in cents.
     * @returns {void}
     */
    setMinAndMaxBetCents: function(minMaxBetCents) {
        this._minBet = minMaxBetCents.minBet;
        this._maxBet = minMaxBetCents.maxBet;
        this.publish("changed");
    },

    /**
     * Fetches the manifest information and resolves with a response object.
     *
     * @private
     * @param {string} url The url from where to fetch the manifest.
     * @returns {deferred} A deferred object.
     */
    fetchManifestData: function(url) {
        return Sys.utils.httpGet({
            url: url
        });
    },

    /**
    * Assigns the game client version by resolving a Deferred object.
    *
    * @private
    * @returns {void}
    */
    assignGameClientVersion: function() {
        var me = this,
            clientManifestFile = this.fetchManifestData(
                this._gameClientManifestURL
            );

        clientManifestFile
            .done(function(response) {
                var gameClientVersionText,
                    text = response.responseText;

                gameClientVersionText = me.getGameClientVersion(text);
                me._gameClientVersion = gameClientVersionText === "" ? null : gameClientVersionText;
            })
            .fail(function() {
                console.error("Failed to fetch manifest file");
            })
            .always(function() {
                me.publish("changed");
            });
    },

    /**
     * Assigns min bet, max bet, game client version and game server version.
     *
     * @private
     * @returns {void}
     */
    assignGameInformation: function() {
        var initResponse = Resources.readData("gameServerInitResponseObject"),
            gameServerVersion,
            minMaxBetValues = this.calculateMinAndMaxBetCents(initResponse);

        gameServerVersion = this.getGameServerVersion(initResponse);

        this._minBet = minMaxBetValues.minBet;
        this._maxBet = minMaxBetValues.maxBet;
        this._gameServerVersion = gameServerVersion;
        this.assignGameClientVersion();
        this.publish("changed");
    },

    /**
     * Calculates the highest and lowest possible bet values based on coin values and bet levels.
     *
     * @private
     * @param {Object} initResponse The server response.
     * @returns {Object} An object with min and max bet values in cents.
     */
    calculateMinAndMaxBetCents: function(initResponse) {
        var customCoinValues = this.getCustomCoinValues(),
            hasCustomCoinValues,
            levels,
            betlineCoins,
            coinValues,
            minBet,
            maxBet,
            betLevels,
            denominations;

        hasCustomCoinValues = customCoinValues.length > 0;
        betLevels = initResponse.betlevel.all;
        denominations = initResponse.denomination.all;

        // Sometimes betLevels will be an array, sometimes it will be a single number (ex: free rounds)
        levels =
            typeof betLevels === "number" ? [betLevels] : betLevels.slice();
        betlineCoins = Services.moneyManager.model.readData("betlineCoins");
        denominations =
            typeof denominations === "number" ?
                [denominations] :
                denominations.slice();

        coinValues = hasCustomCoinValues ?
            customCoinValues :
            denominations.map(function(denomination) {
                return denomination * betlineCoins;
            });

        minBet = levels[0] * coinValues[0];
        maxBet = levels[levels.length - 1] * coinValues[coinValues.length - 1];

        return {
            minBet: minBet,
            maxBet: maxBet
        };
    },

    /**
     * Gets the game client version by looking at the implementation-version field of the manifest file.
     *
     * @private
     * @param {string} manifestText Text from META-INF/MANIFEST.MF.
     * @returns {string} The parsed client version or empty string.
     */
    getGameClientVersion: function(manifestText) {
        var searchText = "Implementation-Version:",
            versionNumberRegexp = ".*",
            clientVersionRegExp;

        clientVersionRegExp = new RegExp(searchText + versionNumberRegexp);

        return (manifestText.match(clientVersionRegExp) || [searchText])[0]
            .split(searchText)[1]
            .trim();
    },

    /**
     * Gets the game server version from the init response.
     *
     * @private
     * @param {string} initResponse The init response from the server.
     * @returns {string|null}} The game servers version from the init response or null.
     */
    getGameServerVersion: function(initResponse) {
        return initResponse.gameServerVersion || null;
    },

    /**
     * Gets the custom coin values provided in the query string or null if they are not among the initial values.
     *
     * @example
     * The values are taken from the query string that is on the format:
     *
     * customConfiguration.coinValues=1%252C5%252C100%252C25%252C50%252C100
     * @param {number[]} initialValues All denominations from server response.
     * @returns {Array} The array containing the custom coin values or empty array if they were invalid.
     */
    getCustomCoinValues: function(initialValues) {
        var queryData = Resources.readData("queryData");

        return Utils.Helpers.getCustomCoinValues(initialValues, queryData);
    }
};

Services.GameInformation = Sys.extend(
    Services.Base,
    Services.GameInformation,
    "Services.GameInformation"
);
